import { useMemo, useRef, useState } from 'react';
import type { AppSummary } from '../lib/api';
import type { InstanceView } from '../lib/instance-view';
import { buildSections, railTargets, type Grouping } from '../lib/grouping';
import { isValidatedInstance } from '../lib/validated';
import '../styles/instances.scss';

interface Props {
  apps: AppSummary[];
  view: InstanceView;
  grouping: Grouping;
  /** False for a customer with a handful of rows — they get the cozy list only. */
  showViewSwitch: boolean;
  /**
   * Customer grouping and the validated markers are both SafetyNet-only, and
   * for the same reason: a customer sees their own sites and already knows
   * whose they are and what they are.
   */
  showGrouping: boolean;
  flagValidated: boolean;
  onViewChange: (view: InstanceView) => void;
  onGroupingChange: (grouping: Grouping) => void;
  onLaunch: (app: AppSummary) => void;
}

/** Enough rows that scanning beats reading, so the filter box earns its space. */
const FILTER_THRESHOLD = 8;

/** Enough rows that A-Z headers and the jump rail are worth their space. */
const GROUP_THRESHOLD = 20;

export function InstancePicker({
  apps,
  view,
  grouping,
  showViewSwitch,
  showGrouping,
  flagValidated,
  onViewChange,
  onGroupingChange,
  onLaunch,
}: Props) {
  const [query, setQuery] = useState('');
  const scrollerRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return apps;
    return apps.filter((app) => app.displayName.toLowerCase().includes(needle));
  }, [apps, query]);

  const sections = useMemo(() => buildSections(matches, grouping), [matches, grouping]);

  const validatedCount = useMemo(
    () => (flagValidated ? apps.filter((app) => isValidatedInstance(app.displayName)).length : 0),
    [apps, flagValidated],
  );

  // Customer grouping is asked for explicitly, so its headings always show. A-Z
  // headings are automatic, and only earn their space on a long list.
  const headed = grouping === 'customer' || apps.length >= GROUP_THRESHOLD;
  const filterable = apps.length >= FILTER_THRESHOLD;
  const rail = useMemo(
    () => (headed ? railTargets(sections) : []),
    [headed, sections],
  );

  // `offsetTop` is measured against the scroller because it is the positioned
  // ancestor. Scrolling it directly rather than calling `scrollIntoView` keeps
  // the jump inside the panel instead of dragging the whole page with it.
  const jumpTo = (key: string) => {
    const scroller = scrollerRef.current;
    const target = scroller?.querySelector<HTMLElement>(`[data-section="${CSS.escape(key)}"]`);
    if (scroller && target) scroller.scrollTop = target.offsetTop;
  };

  return (
    <section className="x5-instances" aria-labelledby="x5-instances-title">
      <div className="x5-instances__head">
        <div className="x5-instances__heading">
          <h1 className="x5-instances__title" id="x5-instances-title">
            Launch Instance
          </h1>
          <p className="x5-instances__subtitle">
            {apps.length === 1
              ? 'Select the instance you want to open.'
              : `${apps.length} instances available to your account.`}
            {validatedCount > 0 && (
              <>
                {' '}
                <span className="x5-instances__count-validated">{validatedCount} validated.</span>
              </>
            )}
          </p>
        </div>

        {(filterable || showViewSwitch || showGrouping) && (
          <div className="x5-instances__tools">
            {filterable && (
              <div className="x5-instances__search">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <circle
                    cx="11"
                    cy="11"
                    r="6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <path
                    d="M16 16l4.5 4.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Filter instances..."
                  aria-label="Filter instances"
                />
              </div>
            )}

            {showGrouping && (
              <button
                type="button"
                className="x5-instances__group-toggle"
                aria-pressed={grouping === 'customer'}
                onClick={() => onGroupingChange(grouping === 'customer' ? 'alpha' : 'customer')}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                  <path
                    d="M4 6h16M7 12h13M7 18h13"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                  />
                </svg>
                Group by customer
              </button>
            )}

            {showViewSwitch && (
              <div className="x5-instances__views" role="group" aria-label="List density">
                <ViewButton current={view} value="cozy" onChange={onViewChange} />
                <ViewButton current={view} value="dense" onChange={onViewChange} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="x5-instances__body">
        <div className={`x5-instances__scroll x5-instances__scroll--${view}`} ref={scrollerRef}>
          {matches.length === 0 ? (
            <p className="x5-instances__empty">No instance matches &ldquo;{query.trim()}&rdquo;.</p>
          ) : (
            <ul className="x5-instances__sections">
              {sections.map((section) => {
                // A customer's sites are nested under its heading so the rows
                // that belong to it are bounded. Without that, a single-site
                // instance — which has no heading of its own, because the
                // heading would only repeat the row — read as one more site of
                // the customer above it: `conception` looked like a
                // Conceivable Life Sciences site, `addgene` like an Abiomed one.
                const nested = grouping === 'customer' && section.items.length > 1;

                return (
                  // The anchor lives on the section, not the heading: a
                  // single-instance customer has no heading to scroll to.
                  <li key={section.key} data-section={section.key}>
                    {headed && section.items.length > 1 && (
                      <p className="x5-instances__group">
                        {section.label}
                        {nested && (
                          <span className="x5-instances__group-count">
                            {section.items.length} instances
                          </span>
                        )}
                      </p>
                    )}
                    {view === 'dense' ? (
                      <ul
                        className={
                          nested
                            ? 'x5-instances__rows x5-instances__rows--nested'
                            : 'x5-instances__rows'
                        }
                      >
                        {section.items.map((app) => (
                          <li key={app.id}>
                            <DenseRow app={app} flagValidated={flagValidated} onLaunch={onLaunch} />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ul
                        className={
                          nested
                            ? 'x5-instances__cards x5-instances__cards--nested'
                            : 'x5-instances__cards'
                        }
                      >
                        {section.items.map((app) => (
                          <li key={app.id}>
                            <CozyCard app={app} flagValidated={flagValidated} onLaunch={onLaunch} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Outside the scroller, as its own column: a rail that scrolled away
            with the rows it indexes would be useless past the first screen. */}
        {rail.length > 1 && (
          <nav className="x5-instances__rail" aria-label="Jump to letter">
            {rail.map(({ letter, key }) => (
              <button type="button" key={letter} onClick={() => jumpTo(key)}>
                {letter}
              </button>
            ))}
          </nav>
        )}
      </div>
    </section>
  );
}

interface ItemProps {
  app: AppSummary;
  flagValidated: boolean;
  onLaunch: (app: AppSummary) => void;
}

function DenseRow({ app, flagValidated, onLaunch }: ItemProps) {
  const validated = flagValidated && isValidatedInstance(app.displayName);

  return (
    <button
      type="button"
      className={validated ? 'x5-instances__row x5-instances__row--validated' : 'x5-instances__row'}
      onClick={() => onLaunch(app)}
    >
      <span className="x5-instances__dot" aria-hidden="true" />
      <span className="x5-instances__name">{app.displayName}</span>
      {validated && <span className="x5-instances__badge">Validated</span>}
      <span className="x5-instances__launch">
        Launch
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path
            d="M4 12h15m-6-6l6 6-6 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  );
}

function CozyCard({ app, flagValidated, onLaunch }: ItemProps) {
  const validated = flagValidated && isValidatedInstance(app.displayName);

  return (
    <button
      type="button"
      className={
        validated ? 'x5-instances__card x5-instances__card--validated' : 'x5-instances__card'
      }
      onClick={() => onLaunch(app)}
    >
      <span>{app.displayName}</span>
      {validated && (
        <span className="x5-instances__badge x5-instances__badge--card">Validated</span>
      )}
    </button>
  );
}

function ViewButton({
  current,
  value,
  onChange,
}: {
  current: InstanceView;
  value: InstanceView;
  onChange: (view: InstanceView) => void;
}) {
  return (
    <button
      type="button"
      className="x5-instances__view"
      aria-pressed={current === value}
      onClick={() => onChange(value)}
    >
      {value === 'cozy' ? 'Cozy' : 'Dense'}
    </button>
  );
}
